import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTranslator } from '@/hooks/useTranslator';
import { useTermsOfUseGate } from './hooks/useTermsOfUseGate';
import { TermsDeclineConfirmDialog } from './TermsDeclineConfirmDialog';
import { TermsDeleteAccountDialog } from './TermsDeleteAccountDialog';

/**
 * App-level Terms of Use gate — the v2 equivalent of the old dashboard's
 * `/terms-of-use` redirect. A non-dismissable dialog (no Escape, no backdrop
 * click, no ✕) blocks the app for any signed-in, non-impersonated user who
 * hasn't accepted the current terms yet. Self-gates like
 * `IntercomConsentBanner` — renders nothing once accepted.
 */
export function TermsOfUseGate() {
  const { getTranslation: t } = useTranslator({ page: 'termsOfUse' });
  const gate = useTermsOfUseGate();

  if (!gate.visible) return null;

  return (
    <>
      <Dialog open onOpenChange={() => undefined} disablePointerDismissal>
        <DialogContent size='sm' showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{t('title')}</DialogTitle>
          </DialogHeader>
          <DialogBody className='flex flex-col gap-3'>
            <p className='text-sm font-medium text-foreground'>
              {t('subheading')}
            </p>
            <p>{t('intro')}</p>
            <div className='flex flex-col gap-1'>
              <a
                href='https://permalink.retraced.com/terms-of-use-v1.0.0.pdf'
                target='_blank'
                rel='noreferrer'
                className='text-primary underline'
              >
                {t('termsLink')}
              </a>
              <a
                href='https://www.retraced.com/pages/privacy-policy'
                target='_blank'
                rel='noreferrer'
                className='text-primary underline'
              >
                {t('privacyLink')}
              </a>
            </div>
          </DialogBody>
          <DialogFooter className='sm:justify-between'>
            <Button variant='destructive' onClick={gate.openDeleteConfirm}>
              {t('deleteAccount')}
            </Button>
            <div className='flex gap-2'>
              <Button variant='outline' onClick={gate.openDeclineConfirm}>
                {t('decline')}
              </Button>
              <Button
                id='agree'
                loading={gate.isAgreeing}
                onClick={() => void gate.agree()}
              >
                {t('agree')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TermsDeclineConfirmDialog
        open={gate.declineConfirmOpen}
        onCancel={gate.closeDeclineConfirm}
        onConfirm={() => void gate.confirmDecline()}
      />
      <TermsDeleteAccountDialog
        open={gate.deleteConfirmOpen}
        confirmText={gate.deleteConfirmText}
        onConfirmTextChange={gate.setDeleteConfirmText}
        isPending={gate.isDeletePending}
        onCancel={gate.closeDeleteConfirm}
        onConfirm={() => void gate.confirmDelete()}
      />
    </>
  );
}
